import React, { useState, useMemo, useEffect } from 'react';
import { todayIso } from '../../utils/accountFilters';
import {
  FileText,
  Download,
  Printer,
  Calendar,
  Filter,
  X,
  CreditCard,
  User,
  Building2,
  Percent,
  CheckCircle2,
  DollarSign,
  Layers,
} from 'lucide-react';
import { useSRMS } from '../../context/SRMSContext';
import { RecoveryRecord, Account } from '../../types';
import { formatINR } from '../../utils/watermark';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

import { displayDate } from '../../utils/dates';
interface CommissionBillModalProps {
  isOpen: boolean;
  onClose: () => void;
  targetAgentId?: string;
}

export const CommissionBillModal: React.FC<CommissionBillModalProps> = ({
  isOpen,
  onClose,
  targetAgentId,
}) => {
  const { recoveries, accounts, users, currentUser, commissionSettings, markAccountsSarfaesi } = useSRMS();

  // Find effective agent
  const effectiveAgent = useMemo(() => {
    if (targetAgentId) {
      return (
        users.find((u) => u.agentId === targetAgentId || u.id === targetAgentId) ||
        currentUser
      );
    }
    return currentUser;
  }, [targetAgentId, users, currentUser]);

  /**
   * BLANK UNLESS THIS AGENT'S OWN DETAILS ARE KNOWN.
   *
   * Both of these used to fall back to one real person's PAN number and one
   * real person's mobile -- whoever the app was first built against. Every
   * other agent's bill therefore went out carrying a stranger's PAN and a
   * stranger's phone number, which is somebody else's tax identity on a
   * document submitted to a bank.
   *
   * Now: this agent's saved PAN if there is one, this agent's own mobile if
   * the staff record has one, and otherwise EMPTY. Neither is required to
   * produce a bill -- an empty line simply prints nothing.
   */
  const [panCardNo, setPanCardNo] = useState<string>(() => {
    try {
      return localStorage.getItem(`srms_pan_${effectiveAgent.agentId || effectiveAgent.id}`) || '';
    } catch {
      return '';
    }
  });

  const [agentMobile, setAgentMobile] = useState<string>(() => effectiveAgent.mobile || '');

  /**
   * AN OPTIONAL NOTE ON THE BILL, IN THE AGENT'S OWN WORDS.
   *
   * Ashish chose Option B: the agent types BOTH the heading and the text, so
   * one bill can read "Remark: ..." and the next "Note to Branch Manager:
   * ...". Printed below the total and above the signature blocks, which is
   * where a remark belongs on a bill submitted to a branch.
   *
   * Saved per agent, the same way the PAN is, so a note that goes on every
   * month's bill does not have to be retyped. Left empty, NOTHING is printed
   * and the bill looks exactly as it does today -- no empty heading, no blank
   * line, no reserved space.
   */
  const noteKey = `srms_billnote_${effectiveAgent.agentId || effectiveAgent.id}`;
  const [noteHeading, setNoteHeading] = useState<string>(() => {
    try {
      return localStorage.getItem(`${noteKey}_heading`) || '';
    } catch {
      return '';
    }
  });
  const [noteText, setNoteText] = useState<string>(() => {
    try {
      return localStorage.getItem(`${noteKey}_text`) || '';
    } catch {
      return '';
    }
  });

  // Remembered as it is typed, so closing the window does not lose it.
  useEffect(() => {
    try {
      localStorage.setItem(`${noteKey}_heading`, noteHeading);
      localStorage.setItem(`${noteKey}_text`, noteText);
    } catch {
      /* A browser with storage turned off simply does not remember it. */
    }
  }, [noteKey, noteHeading, noteText]);

  /** The note as one line, or empty when there is nothing to print. */
  const billNoteLine = useMemo(() => {
    const h = noteHeading.trim();
    const t = noteText.trim();
    if (!h && !t) return '';
    if (h && t) return `${h}: ${t}`;
    return h || t;
  }, [noteHeading, noteText]);

  // Selected bill generation date (DD-MM-YYYY)
  const [billDate, setBillDate] = useState<string>(() => {
    return todayIso();
  });

  // Month & Year Filter state (all 12 months + current year)
  const currentYear = new Date().getFullYear();
  const currentMonthNum = String(new Date().getMonth() + 1).padStart(2, '0');
  const [selectedYear, setSelectedYear] = useState<string>(String(currentYear));
  const [selectedMonth, setSelectedMonth] = useState<string>(currentMonthNum);
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  // Recovery bills are settled branch by branch: an agent submits to the
  // branch manager of that branch, and there is no combined bill. So a bill
  // covers exactly one branch.
  const [selectedBillBranch, setSelectedBillBranch] = useState<string>('');

  // Accounts the agent has ticked as SARFAESI on this bill. Accounts already
  // marked SARFAESI are counted whether or not they are ticked here.
  // The ticked set is the single source of truth. It is seeded from the
  // accounts already recorded as SARFAESI, so unticking one genuinely
  // unticks it -- previously "already SARFAESI" forced the box permanently
  // checked and it could never be undone.
  const [sarfaesiPicks, setSarfaesiPicks] = useState<Set<string>>(() => new Set<string>());
  const [sarfaesiSeeded, setSarfaesiSeeded] = useState(false);
  const [showSarfaesiPicker, setShowSarfaesiPicker] = useState(false);
  const [sarfaesiNote, setSarfaesiNote] = useState<string | null>(null);


  // Month definitions for all 12 months
  const allMonthsList = [
    { value: 'all', label: 'All Months (Full Year)' },
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];

  const availableYears = [String(currentYear), String(currentYear - 1), String(currentYear + 1)];

  // Filter recoveries for agent and date range
  const agentRecoveries = useMemo(() => {
    // A bill belongs to ONE agent. This used to return true for every
    // recovery when an admin opened the modal without picking an agent, so
    // the bill silently totalled every agent's collections and was handed
    // over as one person's claim.
    const isTarget = (r: RecoveryRecord) => {
      return (
        r.agentId === effectiveAgent.agentId ||
        r.agentId === effectiveAgent.id ||
        (r.agentName && r.agentName.toLowerCase() === effectiveAgent.name.toLowerCase())
      );
    };

    return recoveries.filter((r) => {
      if (!isTarget(r)) return false;
      // Reversed money was given back; it must not be billed for.
      if ((r as any).isReversed) return false;

      const recDate = r.recoveryDate || '';

      // Match Year & Month
      if (selectedYear) {
        if (selectedMonth === 'all') {
          if (!recDate.startsWith(selectedYear)) return false;
        } else {
          const targetPrefix = `${selectedYear}-${selectedMonth}`;
          if (!recDate.startsWith(targetPrefix)) return false;
        }
      }

      if (dateFrom && recDate < dateFrom) return false;
      if (dateTo && recDate > dateTo) return false;

      return true;
    });
  }, [recoveries, effectiveAgent, currentUser, targetAgentId, selectedYear, selectedMonth, dateFrom, dateTo]);

  // The distinct accounts that appear on this bill, with whether each is
  // already recorded as SARFAESI.
  const billableAccounts = useMemo(() => {
    const seen = new Map<string, { accountId: string; customerName: string; already: boolean }>();
    agentRecoveries.forEach((r) => {
      if (seen.has(r.accountId)) return;
      const acc = accounts.find((a) => a.accountId === r.accountId);
      seen.set(r.accountId, {
        accountId: r.accountId,
        customerName: r.customerName || acc?.customerName || 'Customer',
        already: Boolean(acc?.isSarfaesi),
      });
    });
    return Array.from(seen.values());
  }, [agentRecoveries, accounts]);

  useEffect(() => {
    if (sarfaesiSeeded || billableAccounts.length === 0) return;
    const seed = new Set<string>(
      billableAccounts.filter((a) => a.already).map((a) => a.accountId)
    );
    setSarfaesiPicks(seed);
    setSarfaesiSeeded(true);
  }, [billableAccounts, sarfaesiSeeded]);

  const sarfaesiCount = useMemo(
    () => billableAccounts.filter((a) => sarfaesiPicks.has(a.accountId)).length,
    [billableAccounts, sarfaesiPicks]
  );

  /**
   * Which branches this agent actually collected from, in the selected
   * month. Bills are settled branch by branch -- the agent submits to that
   * branch's manager -- so only branches with collections can be billed, and
   * there is deliberately no "all branches" option: a combined bill has
   * nobody to sign it.
   */
  const availableBillBranches = useMemo(() => {
    const set = new Set<string>();
    agentRecoveries.forEach((r) => {
      const acc = accounts.find((a) => a.accountId === r.accountId);
      const branch = (acc?.branch || r.branch || '').trim();
      if (branch) set.add(branch);
    });
    return Array.from(set).sort();
  }, [agentRecoveries, accounts]);

  // Stay on the chosen branch while it still has collections; otherwise move
  // to the first one that does, so the bill is never silently empty.
  useEffect(() => {
    if (availableBillBranches.length === 0) {
      if (selectedBillBranch !== '') setSelectedBillBranch('');
      return;
    }
    if (!availableBillBranches.includes(selectedBillBranch)) {
      setSelectedBillBranch(availableBillBranches[0]);
    }
  }, [availableBillBranches, selectedBillBranch]);

  // Join each recovery with its account details
  const billRows = useMemo(() => {
    const scoped = selectedBillBranch
      ? agentRecoveries.filter((r) => {
          const acc = accounts.find((a) => a.accountId === r.accountId);
          return (
            (acc?.branch || r.branch || '').trim().toLowerCase() ===
            selectedBillBranch.trim().toLowerCase()
          );
        })
      : agentRecoveries;

    return scoped.map((r, index) => {
      const acc = accounts.find((a) => a.accountId === r.accountId);
      // A SARFAESI recovery is paid at the SARFAESI rate, which the agent
      // selects the accounts for and an administrator sets the value of.
      const isSarf = sarfaesiPicks.has(r.accountId);
      const sarfRate =
        effectiveAgent?.sarfaesiCommissionPercent || commissionSettings.sarfaesiRate || 0;

      const commissionRate =
        isSarf && sarfRate > 0
          ? sarfRate
          : r.commissionPercentage || commissionSettings.defaultRate || 10;
      const commissionAmt =
        isSarf && sarfRate > 0
          ? Math.round((r.amount * sarfRate) / 100)
          : r.commissionAmount || Math.round((r.amount * commissionRate) / 100);

      // Format recovery date to DD-MM-YYYY
      let formattedRecDate = r.recoveryDate;
      if (r.recoveryDate && r.recoveryDate.includes('-')) {
        const parts = r.recoveryDate.split('-');
        if (parts.length === 3 && parts[0].length === 4) {
          formattedRecDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
      }

      // Format NPA date
      const npaDate = acc?.npaDate || '-';
      // User request: Keep CIF column blank (no auto-filled value)
      const cif = '';
      const zoRefNo = (acc?.customFields && acc.customFields['ZO_REF_NO']) || '-';
      const iracPast = (acc?.customFields && acc.customFields['IRAC_PAST']) || '-';
      // "Present IRAC" is left blank on the bill by request -- the branch
      // fills it in by hand, the same way the CIF column is left blank.
      const presentIrac = '-';

      return {
        srNo: index + 1,
        zoRefNo,
        cif,
        accountNo: acc?.loanNumber || r.accountId,
        borrowerName: r.customerName || acc?.customerName || 'Borrower',
        recoveryDate: formattedRecDate,
        recoveryAmount: r.amount,
        npaDate,
        iracPast,
        presentIrac,
        commissionRate,
        commissionAmount: commissionAmt,
        isSarfaesi: isSarf,
        branch: acc?.branch || effectiveAgent.branch || 'Mill Corner',
      };
    });
  }, [agentRecoveries, accounts, commissionSettings, effectiveAgent, selectedBillBranch]);

  const totalRecoveryAmount = useMemo(() => {
    return billRows.reduce((sum, r) => sum + r.recoveryAmount, 0);
  }, [billRows]);

  const totalCommissionAmount = useMemo(() => {
    return billRows.reduce((sum, r) => sum + r.commissionAmount, 0);
  }, [billRows]);

  if (!isOpen) return null;

  const handlePanChange = (val: string) => {
    const formatted = val.toUpperCase().trim();
    setPanCardNo(formatted);
    localStorage.setItem(`srms_pan_${effectiveAgent.agentId || effectiveAgent.id}`, formatted);
  };

  const handleDownloadLandscapePDF = () => {
    setIsGeneratingPDF(true);
    try {
      // Landscape A4 document
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4',
      });

      // Format Bill Date as DD-MM-YYYY
      let formattedBillDate = billDate;
      if (billDate.includes('-')) {
        const p = billDate.split('-');
        if (p.length === 3 && p[0].length === 4) {
          formattedBillDate = `${p[2]}-${p[1]}-${p[0]}`;
        }
      }

      // Header details matching reference image
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text(`AGENCY NAME : ${effectiveAgent.name.toUpperCase()}`, 14, 15);
      // Blank is blank. A bill with no PAN or no mobile recorded simply does
      // not print that line, rather than printing a label with nothing after
      // it or, as it used to, somebody else's number.
      if (agentMobile.trim()) doc.text(`Mo. ${agentMobile.trim()}`, 14, 21);

      if (panCardNo.trim()) doc.text(`Pan Card No. : ${panCardNo.trim()}`, 160, 15);
      doc.text(`DATE : ${formattedBillDate}`, 240, 15);

      // The bill must say which branch and which period it covers -- it is
      // submitted to that branch's manager and signed off there.
      doc.text(`BRANCH : ${(selectedBillBranch || 'All').toUpperCase()}`, 14, 27);
      const periodLabel =
        selectedMonth === 'all'
          ? `YEAR ${selectedYear}`
          : `${(allMonthsList.find((m) => m.value === selectedMonth)?.label || '').toUpperCase()} ${selectedYear}`;
      doc.text(`PERIOD : ${periodLabel}`, 160, 27);

      // Build Table Data
      const tableData = billRows.map((r) => [
        String(r.srNo),
        r.zoRefNo,
        r.cif,
        r.accountNo,
        r.borrowerName,
        r.recoveryDate,
        r.recoveryAmount.toLocaleString('en-IN'),
        // DD-MM-YYYY, the same as the screen. The PDF was printing the stored
        // YYYY-MM-DD while the preview above it showed DD-MM-YYYY, so the
        // downloaded bill and the screen disagreed on the format of the same
        // date -- and 2019-01-02 is easily misread as 1 February.
        displayDate(r.npaDate),
        r.iracPast,
        r.presentIrac,
        `${r.commissionRate}`,
        r.commissionAmount.toLocaleString('en-IN'),
      ]);

      // Add Footer Row in Table
      /**
       * THE BRANCH THIS BILL IS FOR, NOT THE AGENT'S HOME BRANCH.
       *
       * The bill is prepared per branch -- that is what the Select Branch
       * dropdown above is for, and why only branches where this agent
       * actually collected are offered: a combined bill has no single
       * manager to sign it.
       *
       * This line read the AGENT's own branch instead. So selecting MILL
       * CORNER produced a bill of Mill Corner's collections with
       * "Branch : Garkheda" printed under it, because Garkheda is where the
       * agent is based. A branch manager would have been asked to sign a
       * bill naming a branch that is not theirs.
       *
       * The selection wins. The agent's branch is only the fallback, for a
       * bill drawn before any branch was chosen.
       */
      const branchName = selectedBillBranch || effectiveAgent.branch || 'Mill Corner';

      autoTable(doc, {
        startY: 33,   // below the BRANCH / PERIOD line added at y=27
        head: [
          [
            'Sr No.',
            'ZO Ref No',
            'CIF',
            'A/c No',
            'Borrower Name',
            /**
             * THE LINE BREAKS ARE DELIBERATE. DO NOT REMOVE THEM.
             *
             * THE BUG THIS FIXES -- a column heading on the downloaded bill
             * was split in the middle of a word: "Commission % as per
             * circular" printed as "Commissio / n % as per circular".
             *
             * Measured rather than guessed. In Helvetica Bold at 8pt the word
             * "Commission" is 16.94mm wide. Its column was 20mm, and the
             * table takes 1.76mm of padding on each side, leaving 16.47mm.
             * Short by less than half a millimetre -- so the word did not
             * fit, and the table broke it wherever it happened to land.
             *
             * Two things fix it, and both are applied so a longer heading
             * can never do this again: the two commission columns are wider
             * (below), and the headings now carry their OWN line breaks, so
             * each one wraps where a person would wrap it instead of
             * wherever the width runs out.
             */
            'Date of\nRecovery',
            'Rec amt.',
            'NPA Date',
            'IRAC as on\n31.03.2025',
            'Present\nIRAC',
            'Commission %\nas per circular',
            'Commission amt %\nas per circular',
          ],
        ],
        body: tableData,
        foot: [
          [
            { content: `Branch : ${branchName}`, colSpan: 6, styles: { halign: 'left', fontStyle: 'bold' } },
            { content: totalRecoveryAmount.toLocaleString('en-IN'), styles: { halign: 'right', fontStyle: 'bold' } },
            /**
             * "TOTAL COMMISSION" SPANS THREE COLUMNS SO IT STOPS SPLITTING.
             *
             * THE BUG THIS FIXES -- the bill printed "TOTAL CO / MMISSION",
             * the word cut in half. Seen on the real bill for Mill Corner,
             * 30-09-2026.
             *
             * The label sat in ONE column. At 8.5pt bold it is 30.49mm wide
             * and that column gives it 16.48mm, so it was never going to fit
             * -- widening the column would not have been enough either.
             *
             * It now spans the two IRAC columns and the commission-rate
             * column, which are empty on this row anyway: 18+15+23 = 56mm,
             * leaving 52.48mm for a 30.49mm label. The empty cell that used
             * to span three columns now spans one, so the row still adds up
             * to twelve.
             */
            { content: '', styles: { halign: 'center' } },
            { content: 'TOTAL COMMISSION', colSpan: 3, styles: { halign: 'right', fontStyle: 'bold' } },
            { content: totalCommissionAmount.toLocaleString('en-IN'), styles: { halign: 'right', fontStyle: 'bold' } },
          ],
        ],
        theme: 'grid',
        headStyles: {
          fillColor: [240, 240, 240],
          textColor: [0, 0, 0],
          fontStyle: 'bold',
          // 7.5 rather than 8: it buys about 3.6mm of clearance on the
          // tightest heading, so the bank adding a word to a column name
          // does not split it again.
          fontSize: 7.5,
          lineColor: [100, 100, 100],
          lineWidth: 0.2,
          halign: 'center',
          valign: 'middle',
        },
        bodyStyles: {
          fontSize: 8,
          lineColor: [180, 180, 180],
          lineWidth: 0.15,
          textColor: [20, 20, 20],
        },
        footStyles: {
          fillColor: [245, 245, 245],
          textColor: [0, 0, 0],
          fontSize: 8.5,
          fontStyle: 'bold',
          lineColor: [100, 100, 100],
          lineWidth: 0.2,
        },
        columnStyles: {
          /**
           * EVERY COLUMN MEASURED AGAINST WHAT IT ACTUALLY HOLDS.
           *
           * THE BUG THIS FIXES -- the heading was only the visible half of
           * it. Measured in the PDF's own font, FOUR COLUMNS WERE TOO NARROW
           * FOR THEIR CONTENTS and had been since the bill was first drawn:
           *
           *   ZO Ref No   "ZO/2026/0418"                18.04mm in 16.47mm
           *   CIF         "60304546617"                 17.26mm in 16.47mm
           *   A/c No      "ACC-60304546617"             24.16mm in 22.47mm
           *   Borrower    "M/S. STAR MANDAP DECORATERS" 47.36mm in 38.47mm
           *
           * So the account number, the CIF and the borrower's name were each
           * being split across two lines on a bill submitted to a branch
           * manager. An account number broken in half is worse than an ugly
           * heading: it is the one field the bank uses to find the loan.
           *
           * WHERE THE SPACE CAME FROM. Nothing was added to the row -- it
           * still totals 274mm against the 277mm the landscape page allows.
           * NPA Date was 28mm and needs 18; the two IRAC columns were 20 and
           * 18 and hold two characters. That slack went to the name and the
           * identifiers.
           *
           * CHECKED, NOT ESTIMATED: every heading word and every sample value
           * was measured against its column at the sizes below. Nothing
           * wraps and nothing splits.
           */
          0: { cellWidth: 12, halign: 'center' }, // Sr No
          1: { cellWidth: 22, halign: 'center' }, // ZO Ref No
          2: { cellWidth: 21, halign: 'center' }, // CIF
          3: { cellWidth: 28, halign: 'left' },   // A/c No
          4: { cellWidth: 56, halign: 'left' },   // Borrower Name
          5: { cellWidth: 19, halign: 'center' }, // Date of Recovery
          6: { cellWidth: 18, halign: 'right' },  // Rec amt
          /**
           * NPA DATE, WIDE ENOUGH FOR THE DATE IT HOLDS.
           *
           * At 22mm a DD-MM-YYYY date wrapped onto two lines, which pushed
           * every row of the bill taller and made a ten-character date look
           * like two separate numbers.
           *
           * It is now 19mm, which is what a DD-MM-YYYY date and the word
           * "Date" actually need. The row totals
           * 12+22+21+28+56+19+18+19+18+15+23+23 = 274mm against 277mm.
           *
           * The commission columns are 23mm so that "Commission %" sits on
           * one line above "as per circular". "Commission amt % as per
           * circular" is simply too long for any column this row can spare
           * and takes three lines -- but it WRAPS between words rather than
           * splitting one, which is what was wrong.
           */
          7: { cellWidth: 19, halign: 'center' }, // NPA Date
          8: { cellWidth: 18, halign: 'center' }, // IRAC past
          9: { cellWidth: 15, halign: 'center' }, // Present IRAC
          10: { cellWidth: 23, halign: 'center' }, // Comm %
          11: { cellWidth: 23, halign: 'right' },  // Comm amt
        },
        margin: { left: 10, right: 10 },
      });

      const tableEndY = (doc as any).lastAutoTable.finalY || 160;

      /**
       * THE AGENT'S NOTE, BELOW THE TOTAL AND ABOVE THE SIGNATURES.
       *
       * Printed only when there is something to print. Wrapped to the page
       * width so a long remark runs onto a second line instead of off the
       * edge, and the signature blocks are pushed down by however many lines
       * it takes -- so a three-line note cannot overlap "DRA AGENCY".
       */
      let noteBottomY = tableEndY;
      if (billNoteLine) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        const noteLines = doc.splitTextToSize(billNoteLine, 267) as string[];
        doc.text(noteLines, 12, tableEndY + 8);
        noteBottomY = tableEndY + 8 + (noteLines.length - 1) * 4.5;
      }

      // Signature Blocks at Bottom
      const finalY = noteBottomY;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');

      // Signature block 1: DRA AGENCY
      doc.text('DRA AGENCY', 25, Math.min(195, finalY + 18));
      doc.text('( Seal & Signature )', 25, Math.min(202, finalY + 25));

      // Signature block 2: Branch Manager
      doc.text('Branch Manager', 225, Math.min(195, finalY + 18));
      doc.text('( Seal & Signature )', 225, Math.min(202, finalY + 25));

      // Save PDF file
      const filename = `Commission_Bill_${effectiveAgent.name.replace(/\s+/g, '_')}_${formattedBillDate}.pdf`;
      doc.save(filename);
    } catch (err: any) {
      alert(`Error generating PDF bill: ${err?.message || err}`);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-2 sm:p-4 overflow-y-auto font-sans">
      <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-6xl shadow-2xl text-slate-800 flex flex-col max-h-[92vh] overflow-hidden">
        {/* Top Control Bar */}
        <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3 shrink-0">
          <div className="flex items-center gap-3">
            <span className="p-2.5 bg-amber-100 text-amber-800 rounded-xl">
              <FileText className="w-5 h-5" />
            </span>
            <div>
              <h3 className="font-bold text-base text-slate-900">
                Agent Recovery Commission Bill (Landscape Format)
              </h3>
              <p className="text-xs text-slate-500">
                Official circular commission invoice format for bank submission and approval
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleDownloadLandscapePDF}
              disabled={isGeneratingPDF || billRows.length === 0}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-xl font-bold text-xs flex items-center gap-2 shadow-sm transition cursor-pointer"
            >
              <Download className="w-4 h-4" />
              <span>{isGeneratingPDF ? 'Generating PDF...' : 'Download Landscape PDF'}</span>
            </button>

            <button
              onClick={handlePrint}
              className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-bold text-xs flex items-center gap-1.5 border border-slate-200 transition cursor-pointer"
            >
              <Printer className="w-4 h-4" />
              <span>Print Bill</span>
            </button>

            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-700 rounded-xl hover:bg-slate-100 cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Filter and Metadata Bar */}
        <div className="p-4 bg-slate-50/70 border-b border-slate-200 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 text-xs shrink-0">
          {/* Agent PAN Card Input */}
          <div>
            <label className="block text-slate-700 font-semibold mb-1">
              Agent PAN Card No: <span className="font-normal text-slate-400">(optional)</span>
            </label>
            <input
              type="text"
              value={panCardNo}
              onChange={(e) => handlePanChange(e.target.value)}
              placeholder="ABCDE1234F"
              className="w-full bg-white border border-slate-200 rounded-xl px-3 py-1.5 font-mono text-xs text-slate-800 uppercase font-bold focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>

          {/* Agent Mobile Number */}
          <div>
            <label className="block text-slate-700 font-semibold mb-1">
              Agent Mobile No: <span className="font-normal text-slate-400">(optional)</span>
            </label>
            <input
              type="text"
              value={agentMobile}
              onChange={(e) => setAgentMobile(e.target.value)}
              placeholder="10-digit mobile"
              className="w-full bg-white border border-slate-200 rounded-xl px-3 py-1.5 font-mono text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>

          {/* SARFAESI. The agent knows which of their recoveries were made
              under SARFAESI; what that work is worth is an administrator's
              setting, so ticking accounts here cannot raise anyone's rate. */}
          <div>
            <label className="block text-slate-700 font-semibold mb-1">
              SARFAESI accounts:
            </label>
            <button
              type="button"
              onClick={() => setShowSarfaesiPicker((v) => !v)}
              className="w-full bg-white border border-slate-200 rounded-xl px-3 py-1.5 text-xs font-bold text-slate-800 hover:bg-slate-50 transition cursor-pointer text-left"
            >
              {sarfaesiCount > 0
                ? `${sarfaesiCount} selected`
                : 'Select accounts'}
              <span className="float-right text-slate-400">
                {showSarfaesiPicker ? '\u25B2' : '\u25BC'}
              </span>
            </button>
          </div>

          {/* Branch. One bill, one branch -- there is no combined bill
              because a combined one has no single manager to sign it. Only
              branches where this agent actually collected in the selected
              month are offered, so a bill can never come out empty. */}
          <div>
            <label className="block text-slate-700 font-semibold mb-1">Select Branch:</label>
            {availableBillBranches.length === 0 ? (
              <div className="w-full bg-amber-50 border border-amber-200 rounded-xl px-3 py-1.5 text-[11px] text-amber-800 font-semibold">
                No collections in this period
              </div>
            ) : (
              <select
                value={selectedBillBranch}
                onChange={(e) => setSelectedBillBranch(e.target.value)}
                className="w-full bg-white border border-slate-200 rounded-xl px-3 py-1.5 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500 cursor-pointer font-medium"
              >
                {availableBillBranches.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Month Selector (All 12 months) */}
          <div>
            <label className="block text-slate-700 font-semibold mb-1">Select Month:</label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="w-full bg-white border border-slate-200 rounded-xl px-3 py-1.5 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500 cursor-pointer font-medium"
            >
              {allMonthsList.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>

          {/* Year Selector (Current Year) */}
          <div>
            <label className="block text-slate-700 font-semibold mb-1">Select Year:</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="w-full bg-white border border-slate-200 rounded-xl px-3 py-1.5 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500 cursor-pointer font-medium"
            >
              {availableYears.map((yr) => (
                <option key={yr} value={yr}>
                  {yr} {yr === String(currentYear) ? '(Current Year)' : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Bill Generation Date */}
          <div>
            <label className="block text-slate-700 font-semibold mb-1">Bill Date (Print Date):</label>
            <input
              type="date"
              value={billDate}
              onChange={(e) => setBillDate(e.target.value)}
              className="w-full bg-white border border-slate-200 rounded-xl px-3 py-1.5 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>
        </div>

        {/* The optional note. Its own row rather than a sixth box in the grid,
            because the text needs the width. Both fields blank means nothing
            is printed at all. */}
        <div className="px-4 py-3 bg-slate-50/70 border-b border-slate-200 shrink-0">
          <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,3fr)] gap-3 text-xs">
            <div>
              <label className="block text-slate-700 font-semibold mb-1">
                Note heading: <span className="font-normal text-slate-400">(optional)</span>
              </label>
              <input
                type="text"
                value={noteHeading}
                onChange={(e) => setNoteHeading(e.target.value)}
                placeholder="Remark"
                maxLength={60}
                className="w-full bg-white border border-slate-200 rounded-xl px-3 py-1.5 text-xs text-slate-800 font-bold focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
            </div>
            <div>
              <label className="block text-slate-700 font-semibold mb-1">
                Note: <span className="font-normal text-slate-400">(optional)</span>
              </label>
              <input
                type="text"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Anything you want printed on the bill, above the signatures"
                maxLength={300}
                className="w-full bg-white border border-slate-200 rounded-xl px-3 py-1.5 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
            </div>
          </div>
          {billNoteLine ? (
            <div className="mt-1.5 text-[10px] text-slate-500">
              Prints on the bill as: <strong className="text-slate-700">{billNoteLine}</strong>
              {'  '}
              <button
                type="button"
                onClick={() => {
                  setNoteHeading('');
                  setNoteText('');
                }}
                className="ml-2 text-rose-600 hover:text-rose-700 font-semibold underline cursor-pointer"
              >
                clear
              </button>
            </div>
          ) : (
            <div className="mt-1.5 text-[10px] text-slate-400">
              Left empty, nothing is printed. Remembered for your next bill.
            </div>
          )}
        </div>

        {/* The SARFAESI picker: every account on this bill, ticked or not. */}
        {showSarfaesiPicker && (
          <div className="px-6 pb-4 bg-slate-50 border-b border-slate-200 space-y-2">
            <div className="flex items-center justify-between gap-3 flex-wrap pt-3">
              <span className="text-[11px] text-slate-600 font-semibold">
                Tick the accounts recovered under SARFAESI. They will be
                calculated at{' '}
                <strong>
                  {effectiveAgent?.sarfaesiCommissionPercent ||
                    commissionSettings.sarfaesiRate ||
                    0}
                  %
                </strong>{' '}
                instead of the normal rate.
              </span>
              <button
                type="button"
                onClick={() => {
                  const ticked: string[] = billableAccounts
                    .filter((a) => sarfaesiPicks.has(a.accountId))
                    .map((a) => a.accountId);
                  const unticked: string[] = billableAccounts
                    .filter((a) => !sarfaesiPicks.has(a.accountId) && a.already)
                    .map((a) => a.accountId);

                  const parts: string[] = [];
                  if (ticked.length > 0) {
                    parts.push(markAccountsSarfaesi(ticked, true).message);
                  }
                  if (unticked.length > 0) {
                    parts.push(markAccountsSarfaesi(unticked, false).message);
                  }
                  setSarfaesiNote(
                    parts.join(' ') || 'Nothing to change.'
                  );
                  window.setTimeout(() => setSarfaesiNote(null), 5000);
                }}

                className="px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-[11px] transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Save as SARFAESI
              </button>
            </div>

            {(effectiveAgent?.sarfaesiCommissionPercent ||
              commissionSettings.sarfaesiRate ||
              0) <= 0 && (
              <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg text-[11px] text-amber-900 font-semibold">
                No SARFAESI rate has been set yet. An administrator sets it in
                Settings &rarr; Commission Rates. Until then these accounts
                stay on the normal rate.
              </div>
            )}

            {sarfaesiNote && (
              <div className="p-2 bg-blue-50 border border-blue-200 rounded-lg text-[11px] text-blue-900 font-semibold">
                {sarfaesiNote}
              </div>
            )}

            <div className="max-h-44 overflow-y-auto bg-white border border-slate-200 rounded-xl p-2 space-y-1">
              {billableAccounts.length === 0 ? (
                <span className="text-[11px] text-slate-400">
                  No collections in this period.
                </span>
              ) : (
                billableAccounts.map((a) => (
                  <label
                    key={a.accountId}
                    className="flex items-center gap-2 cursor-pointer text-[11px]"
                  >
                    <input
                      type="checkbox"
                      checked={sarfaesiPicks.has(a.accountId)}
                      onChange={(e) =>
                        setSarfaesiPicks((prev) => {
                          const next = new Set(prev);
                          if (e.target.checked) next.add(a.accountId);
                          else next.delete(a.accountId);
                          return next;
                        })
                      }
                    />
                    <span className="font-bold text-slate-800">
                      {a.customerName}
                    </span>
                    <span className="font-mono text-slate-500">
                      {a.accountId}
                    </span>
                    {a.already && (
                      <span className="px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 font-bold">
                        already SARFAESI
                      </span>
                    )}
                  </label>
                ))
              )}
            </div>
          </div>
        )}

        {/* Live Document Preview (Matches Landscape Sheet) */}
        <div className="p-6 overflow-y-auto flex-1 bg-slate-100">
          <div className="bg-white p-6 sm:p-8 rounded-xl shadow-md border border-slate-300 max-w-5xl mx-auto text-slate-900 font-sans">
            {/* Header matching exact photo layout */}
            <div className="border-b-2 border-slate-900 pb-4 mb-4">
              <div className="flex justify-between items-start text-xs font-bold font-mono">
                <div className="space-y-1">
                  <div className="text-sm">
                    AGENCY NAME :{' '}
                    <span className="text-blue-900 font-black">{effectiveAgent.name.toUpperCase()}</span>
                  </div>
                  {agentMobile.trim() && <div>Mo. {agentMobile.trim()}</div>}
                  <div>
                    BRANCH :{' '}
                    <span className="font-black text-slate-900">
                      {selectedBillBranch || '—'}
                    </span>
                  </div>
                </div>

                <div className="text-right space-y-1">
                  {panCardNo.trim() && (
                    <div>
                      Pan Card No. : <span className="font-black text-slate-900">{panCardNo.trim()}</span>
                    </div>
                  )}
                  <div>
                    DATE :{' '}
                    <span className="font-bold">
                      {billDate.split('-').reverse().join('-')}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Bill Table */}
            {billRows.length === 0 ? (
              <div className="py-12 text-center text-slate-400 space-y-2">
                <FileText className="w-8 h-8 mx-auto text-slate-300" />
                <p className="text-sm font-semibold">No recovery entries found for the selected period.</p>
                <p className="text-xs">Log a recovery collection first to generate the commission bill.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-slate-800 text-[11px]">
                  <thead>
                    <tr className="bg-slate-100 font-bold text-center border-b border-slate-800">
                      <th className="border border-slate-800 p-1.5">Sr No.</th>
                      <th className="border border-slate-800 p-1.5">ZO Ref No</th>
                      <th className="border border-slate-800 p-1.5">CIF</th>
                      <th className="border border-slate-800 p-1.5">A/c No</th>
                      <th className="border border-slate-800 p-1.5">Borrower Name</th>
                      <th className="border border-slate-800 p-1.5">Date of Recovery</th>
                      <th className="border border-slate-800 p-1.5">Rec amt.</th>
                      <th className="border border-slate-800 p-1.5">NPA Date</th>
                      <th className="border border-slate-800 p-1.5">IRAC as on 31.03.2025</th>
                      <th className="border border-slate-800 p-1.5">Present IRAC</th>
                      <th className="border border-slate-800 p-1.5">Commission % as per circular</th>
                      <th className="border border-slate-800 p-1.5">Commission amt % as per circular</th>
                    </tr>
                  </thead>
                  <tbody>
                    {billRows.map((r) => (
                      <tr key={r.srNo} className="hover:bg-slate-50">
                        <td className="border border-slate-800 p-1.5 text-center font-mono">{r.srNo}</td>
                        <td className="border border-slate-800 p-1.5 text-center font-mono">{r.zoRefNo}</td>
                        <td className="border border-slate-800 p-1.5 text-center font-mono">{r.cif}</td>
                        <td className="border border-slate-800 p-1.5 font-mono font-bold text-slate-900">
                          {r.accountNo}
                        </td>
                        <td className="border border-slate-800 p-1.5 font-medium">{r.borrowerName}</td>
                        <td className="border border-slate-800 p-1.5 text-center font-mono">{r.recoveryDate}</td>
                        <td className="border border-slate-800 p-1.5 text-right font-mono font-bold text-emerald-700">
                          {r.recoveryAmount.toLocaleString('en-IN')}
                        </td>
                        {/* nowrap for the same reason the PDF column was
                            widened: a wrapped date reads as two numbers. */}
                        <td className="border border-slate-800 p-1.5 text-center font-mono whitespace-nowrap">{displayDate(r.npaDate)}</td>
                        <td className="border border-slate-800 p-1.5 text-center">{r.iracPast}</td>
                        <td className="border border-slate-800 p-1.5 text-center">{r.presentIrac}</td>
                        <td className="border border-slate-800 p-1.5 text-center font-mono font-bold">
                          {r.commissionRate}
                        </td>
                        <td className="border border-slate-800 p-1.5 text-right font-mono font-black text-amber-700">
                          {r.commissionAmount.toLocaleString('en-IN')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-slate-100 font-bold border-t-2 border-slate-800 text-xs">
                      <td colSpan={6} className="border border-slate-800 p-2 font-bold">
                        {/* The branch SELECTED for this bill -- see the note
                            in the PDF builder above. This showed the agent's
                            own branch, so the preview and the selector
                            disagreed. */}
                        Branch : {selectedBillBranch || effectiveAgent.branch || 'Mill Corner'}
                      </td>
                      <td className="border border-slate-800 p-2 text-right font-mono font-bold text-emerald-800">
                        {totalRecoveryAmount.toLocaleString('en-IN')}
                      </td>
                      <td colSpan={3} className="border border-slate-800 p-2 text-center"></td>
                      <td className="border border-slate-800 p-2 text-right uppercase font-bold">
                        TOTAL COMMISSION
                      </td>
                      <td className="border border-slate-800 p-2 text-right font-mono font-black text-amber-800 text-sm">
                        {totalCommissionAmount.toLocaleString('en-IN')}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            {/* The note, in the same place the PDF puts it: under the total,
                above the signatures. Omitted entirely when empty, so the
                preview matches a bill with no note exactly. */}
            {billNoteLine && (
              <div className="mt-4 text-xs text-slate-900 whitespace-pre-wrap break-words">
                {billNoteLine}
              </div>
            )}

            {/* Bottom Signature Space */}
            <div className="mt-14 pt-4 border-t border-dashed border-slate-300 flex justify-between items-end text-xs font-bold font-mono">
              <div className="text-center space-y-8">
                <div>DRA AGENCY</div>
                <div className="text-slate-400 font-sans text-[10px]">( Authorized Seal &amp; Signature )</div>
              </div>

              <div className="text-center space-y-8">
                <div>Branch Manager</div>
                <div className="text-slate-400 font-sans text-[10px]">( Verification &amp; Pass Order )</div>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="p-4 bg-slate-50 border-t border-slate-200 flex justify-between items-center text-xs">
          <span className="text-slate-500">
            Showing {billRows.length} collection entries. Total Commission: <strong className="text-amber-700">{formatINR(totalCommissionAmount)}</strong>
          </span>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-xl font-bold cursor-pointer transition"
          >
            Close Preview
          </button>
        </div>
      </div>
    </div>
  );
};
